import { getStore, getState } from '../state/store';
import Config from './config';
import enable from '../state/reducer/maintenance/actions/enable';
import { purge } from '../state/reducer/app/appState';

const HTTP_CODES = {
    SERVER_ERROR: 500,
    SERVICE_UNAVAILABLE: 503,
    FORBIDDEN: 403
};

const SERVICE_UNDER_MAINTENANCE = 'Service Under Maintenance';

const isInMaintenanceMode = () => getState().maintenance.enable;

const responseHandler = async (response, onFail) => {
    if (response.status === HTTP_CODES.FORBIDDEN) {
        purge();
        return;
    }

    if (response.status === HTTP_CODES.SERVER_ERROR) {
        if (onFail) return onFail();
    }

    // Make a best effort to parse the data as JSON, if not just
    // return the raw text.
    let retVal;
    try {
        retVal = await response.text();
        retVal = JSON.parse(retVal);
    } catch (e) {
        retVal = response;
    }

    if (response.status === HTTP_CODES.SERVICE_UNAVAILABLE) {
        // If we recieved a 503, the maintenance API has responded.
        // The response from that server contains a page_url which
        // points to the maintenance page.
        getStore().dispatch(enable(Config.API));
    }

    return retVal;
};

export async function authenticatedFileUpload(url, file) {
    if (isInMaintenanceMode()) {
        return { message: SERVICE_UNDER_MAINTENANCE };
    }

    const sessionToken = getState().auth.token;
    if (!sessionToken) {
        return { message: 'Not Authenticated' };
    }

    const authedOptions = {
        uploadUrl: urlFormatter(url),
        method: 'POST',
        headers: {
            'Session-Token': sessionToken.session_id
        },
        body: file
    };
    const response = await fetch(urlFormatter(url), authedOptions);

    return responseHandler(response);
}

export async function authenticatedFetch(
    url,
    options = { method: 'GET' },
    onFail
) {
    if (isInMaintenanceMode()) {
        return { message: SERVICE_UNDER_MAINTENANCE };
    }
    const sessionToken = getState().auth.token;

    if (!sessionToken) {
        console.error('No session found');
        return { message: 'Not Authenticated' };
    }
    // Okay let's send a request! First let's get create a new object with
    // all of the request options. We are going to take that and inject
    // the Session-Token into the headers and then send the request. Because
    // these are all API requests, we are expecting JSON respones back
    // however we will
    // default to text if the response is not parseable.
    const authedOptions = { ...options };

    if (!authedOptions.headers) {
        authedOptions.headers = {};
    }
    // authedOptions.headers['Api-Token'] = sessionToken.api_token;
    authedOptions.headers['Api-Token'] = 'DISABLED';
    authedOptions.headers['Session-Token'] = sessionToken.session_id;

    // If the request is passing a body we need to specify the content
    // type so that the
    // API can know how to parse the data. If the content type is set
    // by the caller, we won't
    // touch it. Callers may want to specify their own, for example
    // with multipart form data
    // or octet-stream requests.
    if (
        options.method !== 'GET' &&
        authedOptions.headers['Content-Type'] === undefined
    ) {
        authedOptions.headers['Content-Type'] = 'application/json';
    }

    let response;
    try {
        response = await fetch(urlFormatter(url), authedOptions);
    } catch (error) {
        if (onFail) return onFail();
        return { hasHttpError: true, message: error.message };
    }

    const result = await responseHandler(response, onFail);

    if (result !== undefined && result !== null && typeof result === 'object') {
        result.hasHttpError = !response.ok;
    }

    return result;
}

export async function authenticatedPost(path, payload) {
    const requestOptions = {
        method: 'POST',
        body: JSON.stringify(payload)
    };
    return await authenticatedFetch(path, requestOptions);
}

export async function authenticatedPut(path, payload) {
    const requestOptions = {
        method: 'PUT',
        body: JSON.stringify(payload)
    };
    return await authenticatedFetch(path, requestOptions);
}

export async function authenticatedDelete(path) {
    const requestOptions = {
        method: 'DELETE'
    };
    return await authenticatedFetch(path, requestOptions);
}

// Pretty much the same as authenticated, except doesn't check for
// auth first
// Can only hit public routes (like reset password)
export async function unauthenticatedFetch(url, options = { method: 'GET' }) {
    if (isInMaintenanceMode()) {
        return { message: SERVICE_UNDER_MAINTENANCE };
    }

    // Okay let's send a request! First let's get create a new object with
    // all of the request options. We are going to take that and inject
    // the Session-Token into the headers and then send the request. Because
    // these are all API requests, we are expecting JSON respones back
    // however we will
    // default to text if the response is not parseable.
    const authedOptions = { ...options };

    if (!authedOptions.headers) {
        authedOptions.headers = {};
    }
    // If the request is passing a body we need to specify the content type so
    // that the
    // API can know how to parse the data. If the content type is set by the
    // caller, we won't
    // touch it. Callers may want to specify their own, for example with
    // multipart form data
    // or octet-stream requests.
    if (
        options.method !== 'GET' &&
        authedOptions.headers['Content-Type'] === undefined
    ) {
        authedOptions.headers['Content-Type'] = 'application/json';
    }

    const response = await fetch(urlFormatter(url), authedOptions);

    return responseHandler(response);
}

export const urlFormatter = path => {
    if (path.indexOf('http://') === 0 || path.indexOf('https://') === 0) {
        return path;
    }
    return `${Config.API}${path}`;
};



// WEBPACK FOOTER //
// ./src/api/request.js